/**
 * AutomationPage — Sprint 6.0 (Automação / Playbooks IF-THEN).
 *
 * Aba "Automação" no NOC. Motor de regras IF/THEN com salvaguardas.
 *  - Cards: Regras ativas · Execuções hoje · Dry-run · Falhas.
 *  - Tabela de regras: Nome · Trigger · Ação · Cooldown · Dry-run ·
 *    Status. Ações: Criar · Editar · Ativar/desativar · Executar.
 *  - Tabela de execuções: Data · Regra · Entidade · Status ·
 *    Resultado.
 *
 * Polling de 20s.
 */
import { useCallback, useEffect, useState } from "react";
import {
  createAutomationRule,
  listAutomationRules,
  listAutomationRuns,
  runAutomationRule,
  scanAutomation,
  updateAutomationRule,
} from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";

const POLL_MS = 20000;

const TRIGGERS = [
  "device_offline", "health_critical", "work_order_late",
];
const ACTIONS = ["raise_alert", "notify", "relay_reboot"];

const STATUS_STYLE = {
  success: "text-noc-ok",
  failed: "text-noc-err",
  skipped: "text-noc-muted",
  dry_run: "text-noc-accent",
  pending: "text-noc-warn",
};

export default function AutomationPage() {
  const [data, setData] = useState({
    rules: [], total: 0, enabled_count: 0, dry_run_count: 0,
    runs_today: 0, failures_today: 0,
  });
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [modal, setModal] = useState(null); // 'new' | 'edit'
  const [editRule, setEditRule] = useState(null);

  const load = useCallback((signal) => {
    Promise.all([
      listAutomationRules({ signal }),
      listAutomationRuns({ limit: 50 }, { signal }),
    ])
      .then(([d, r]) => {
        setData(d);
        setRuns(r.runs || []);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  async function toggleEnabled(rule) {
    try {
      await updateAutomationRule(rule.id, { enabled: !rule.enabled });
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  async function runNow(rule) {
    try {
      const res = await runAutomationRule(rule.id, false);
      setNotice(t("automation.runDone") + ` — ${res.summary}`);
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  async function doScan() {
    try {
      const res = await scanAutomation();
      setNotice(
        t("automation.scanDone")
        + ` (${res.evaluated_rules} regras, ${res.runs_created} execuções)`
      );
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-noc-text">
          {t("automation.title")}
        </h1>
        <div className="flex gap-2">
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={doScan}
          >
            {t("automation.btnScan")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={() => setModal("new")}
          >
            + {t("automation.btnNew")}
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <DashCard label={t("automation.cardEnabled")}
                  value={data.enabled_count} tone="emerald" />
        <DashCard label={t("automation.cardRunsToday")}
                  value={data.runs_today} tone="cyan" />
        <DashCard label={t("automation.cardDryRun")}
                  value={data.dry_run_count} tone="sky" />
        <DashCard label={t("automation.cardFailures")}
                  value={data.failures_today} tone="red" />
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">{err}</div>
      )}
      {notice && (
        <div className="text-xs text-noc-ok bg-noc-ok/10
                        border border-noc-ok/30 rounded p-2">
          {notice}
        </div>
      )}

      {/* Tabela de regras */}
      <h2 className="text-sm font-semibold text-noc-text mt-2">
        {t("automation.rulesTitle")}
      </h2>
      <div className="overflow-x-auto card-pad">
        <table className="w-full text-xs">
          <thead className="text-noc-muted border-b border-noc-border">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colName")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colTrigger")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colAction")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colCooldown")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colDryRun")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colStatus")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-sm text-noc-muted/70
                italic py-3 px-2">{t("common.loading")}</td></tr>
            )}
            {!loading && data.rules.length === 0 && (
              <tr><td colSpan={7} className="text-sm text-noc-muted/60
                italic py-3 px-2">{t("automation.empty")}</td></tr>
            )}
            {data.rules.map((r) => (
              <tr key={r.id}
                  className={"border-b border-noc-border/40 "
                    + "hover:bg-noc-bg/30 "
                    + (r.enabled ? "" : "opacity-50")}>
                <td className="py-1.5 px-2 text-noc-text font-medium">
                  {r.name}
                </td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {t("automation.trigger_" + r.trigger_type)
                    || r.trigger_type}
                </td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {t("automation.action_" + r.action_type)
                    || r.action_type}
                </td>
                <td className="py-1.5 px-2">{r.cooldown_seconds}s</td>
                <td className="py-1.5 px-2">
                  {r.dry_run
                    ? <span className="text-noc-accent">DRY</span>
                    : <span className="text-noc-ok">LIVE</span>}
                </td>
                <td className="py-1.5 px-2">
                  {r.enabled
                    ? <span className="text-noc-ok">●</span>
                    : <span className="text-noc-muted">○</span>}
                </td>
                <td className="py-1.5 px-2">
                  <div className="flex gap-1">
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => { setEditRule(r); setModal("edit"); }}
                    >
                      {t("automation.btnEdit")}
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => toggleEnabled(r)}
                    >
                      {r.enabled
                        ? t("automation.btnDisable")
                        : t("automation.btnEnable")}
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-accent/30 text-noc-accent
                                 hover:bg-noc-accent/10"
                      onClick={() => runNow(r)}
                    >
                      {t("automation.btnRun")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tabela de execuções */}
      <h2 className="text-sm font-semibold text-noc-text mt-3">
        {t("automation.runsTitle")}
      </h2>
      <div className="overflow-x-auto card-pad">
        <table className="w-full text-xs">
          <thead className="text-noc-muted border-b border-noc-border">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colDate")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colEntity")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colStatus")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("automation.colResult")}
              </th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr><td colSpan={4} className="text-sm text-noc-muted/60
                italic py-3 px-2">{t("automation.runsEmpty")}</td></tr>
            )}
            {runs.map((run) => (
              <tr key={run.id}
                  className="border-b border-noc-border/40
                             hover:bg-noc-bg/30">
                <td className="py-1.5 px-2 text-noc-muted whitespace-nowrap">
                  {formatRelative(run.started_at)}
                </td>
                <td className="py-1.5 px-2">
                  <span className="text-noc-text">
                    {run.entity_type || "—"}
                  </span>
                  {run.entity_id && (
                    <span className="text-noc-muted/70 font-mono
                                     text-[10px] ml-1">
                      {run.entity_id}
                    </span>
                  )}
                </td>
                <td className={"py-1.5 px-2 "
                  + (STATUS_STYLE[run.status] || "")}>
                  {t("automation.status_" + run.status) || run.status}
                </td>
                <td className="py-1.5 px-2 text-noc-muted/80">
                  {run.result || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === "new" && (
        <RuleFormModal
          title={t("automation.btnNew")}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await createAutomationRule(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "edit" && editRule && (
        <RuleFormModal
          title={t("automation.btnEdit")}
          initial={editRule}
          onClose={() => { setModal(null); setEditRule(null); }}
          onSubmit={async (body) => {
            await updateAutomationRule(editRule.id, body);
            setModal(null);
            setEditRule(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    emerald: "border-noc-ok/30 text-noc-ok",
    cyan: "border-noc-accent/30 text-noc-accent",
    sky: "border-noc-accent/30 text-noc-accent",
    red: "border-noc-err/30 text-noc-err",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`rounded border ${toneCls} bg-noc-bg/30 p-2`}>
      <div className="font-mono text-lg font-bold">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">{label}</div>
    </div>
  );
}

/**
 * Modal de criação/edição de regra IF/THEN. Sem <form>; submit por
 * botão. dry_run nasce marcado — uma regra nova nunca age sem o
 * operador desligar o dry-run conscientemente.
 */
function RuleFormModal({ title, initial, onClose, onSubmit }) {
  const TRIGGERS = ["device_offline", "health_critical",
    "work_order_late"];
  const ACTIONS = ["raise_alert", "notify", "relay_reboot"];
  const [v, setV] = useState(() => ({
    name: (initial && initial.name) || "",
    trigger_type: (initial && initial.trigger_type) || "device_offline",
    action_type: (initial && initial.action_type) || "raise_alert",
    cooldown_seconds: initial && initial.cooldown_seconds != null
      ? String(initial.cooldown_seconds) : "300",
    max_runs_per_day: initial && initial.max_runs_per_day != null
      ? String(initial.max_runs_per_day) : "50",
    window_start: (initial && initial.window_start) || "",
    window_end: (initial && initial.window_end) || "",
    dry_run: initial ? !!initial.dry_run : true,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: v.name,
        trigger_type: v.trigger_type,
        action_type: v.action_type,
        cooldown_seconds: Number(v.cooldown_seconds) || 0,
        max_runs_per_day: Number(v.max_runs_per_day) || 1,
        dry_run: v.dry_run,
      };
      if (v.window_start) body.window_start = v.window_start;
      if (v.window_end) body.window_end = v.window_end;
      await onSubmit(body);
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-2.5 max-h-[90vh]
                      overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">{title}</h3>
        <Field label={t("automation.field_name")}>
          <input value={v.name}
            onChange={(e) => set("name", e.target.value)}
            className="w-full text-xs bg-noc-bg/60 border
                       border-noc-border rounded px-2 py-1 text-noc-text" />
        </Field>
        <Field label={t("automation.field_trigger")}>
          <select value={v.trigger_type}
            onChange={(e) => set("trigger_type", e.target.value)}
            className="w-full text-xs bg-noc-bg/60 border
                       border-noc-border rounded px-2 py-1 text-noc-text">
            {TRIGGERS.map((tr) => (
              <option key={tr} value={tr}>
                {t("automation.trigger_" + tr)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("automation.field_action")}>
          <select value={v.action_type}
            onChange={(e) => set("action_type", e.target.value)}
            className="w-full text-xs bg-noc-bg/60 border
                       border-noc-border rounded px-2 py-1 text-noc-text">
            {ACTIONS.map((ac) => (
              <option key={ac} value={ac}>
                {t("automation.action_" + ac)}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("automation.field_cooldown")}>
            <input type="number" value={v.cooldown_seconds}
              onChange={(e) => set("cooldown_seconds", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text" />
          </Field>
          <Field label={t("automation.field_maxRuns")}>
            <input type="number" value={v.max_runs_per_day}
              onChange={(e) => set("max_runs_per_day", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("automation.field_windowStart")}>
            <input value={v.window_start} placeholder="HH:MM"
              onChange={(e) => set("window_start", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text" />
          </Field>
          <Field label={t("automation.field_windowEnd")}>
            <input value={v.window_end} placeholder="HH:MM"
              onChange={(e) => set("window_end", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text" />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-xs text-noc-text">
          <input type="checkbox" checked={v.dry_run}
            onChange={(e) => set("dry_run", e.target.checked)} />
          {t("automation.field_dryRun")}
        </label>
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-accent/30
                       text-noc-accent bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-noc-muted block mb-0.5">
        {label}
      </label>
      {children}
    </div>
  );
}
